import { YieldFetcher } from '../../types'

/**
 * Lombard LBTC estimated APY. The public analytics endpoint returns a
 * single `lbtc_estimated_apy` as a **fraction** (e.g. 0.00332 for
 * 0.33%), so it is multiplied by 100 to match the percent convention
 * the rest of the intrinsic-yield fetchers use. `partner_id` is
 * accepted but optional.
 */
const LBTC_APY_URL =
  'https://mainnet.prod.lombard.finance/api/v1/analytics/estimated-apy'

export const lombardLbtcFetcher: YieldFetcher = {
  label: 'LBTC',
  fetch: async () => {
    const res = await fetch(LBTC_APY_URL, {
      headers: { accept: 'application/json' },
    }).then((r) => r.json())
    const frac = Number(res?.lbtc_estimated_apy)
    return { LBTC: Number.isFinite(frac) ? frac * 100 : 0 }
  },
}

/**
 * Lombard **DeFi vault** APY (distinct from LBTC staking above). The
 * `vault/apy/history` endpoint returns `snapshots[]` newest-first, each with a
 * `total_apy` **fraction** plus a per-asset `breakdown` (e.g. base LBTCv yield +
 * BARD rewards). We take the latest snapshot's `total_apy × 100` for the percent
 * convention.
 *
 * `wrapper` is the protobuf enum the API keys on (e.g. `VAULT_WRAPPER_BTCE`);
 * `key` is the LST registry `yieldKey` the result is mapped onto. Factory so
 * each Lombard vault gets its own fetcher (matches `createWeethFetcher`).
 */
const LOMBARD_VAULT_APY_URL =
  'https://mainnet.prod.lombard.finance/api/v1/analytics/vault/apy/history'

export const createLombardVaultFetcher = (
  wrapper: string,
  key: string,
): YieldFetcher => ({
  label: `LOMBARD_VAULT:${wrapper}`,
  fetch: async () => {
    const url = `${LOMBARD_VAULT_APY_URL}?vault_wrapper=${wrapper}&interval=APY_INTERVAL_1M`
    const res = await fetch(url, {
      headers: { accept: 'application/json' },
    }).then((r) => r.json())
    // `snapshots` are newest-first; `total_apy` is a fraction.
    const latest = Array.isArray(res?.snapshots) ? res.snapshots[0] : undefined
    const frac = Number(latest?.total_apy)
    return { [key]: Number.isFinite(frac) ? frac * 100 : 0 }
  },
})

/** Lombard LBTCv DeFi vault (the `BTCe` wrapper) APY → `LBTCv` yield key. */
export const lombardLbtcvFetcher = createLombardVaultFetcher(
  'VAULT_WRAPPER_BTCE',
  'LBTCv',
)
